import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { queries } from '~extension/web2print/controller/api/Queries';
import { type JobDownloadFormat } from '~extension/web2print/model/Job';
import { MyFilesDetail } from '~extension/web2print/view/MyFiles/MyFilesDetail';
import useAppDispatch from '~hooks/useAppDispatch';
import { StatusActions } from '~store/actions';

type MyFilesDetailControllerProps = {
  jobId: number;
  documentId: number;
  clientId: number;
  downloadFormat: JobDownloadFormat;
  documentName: string;
};

export function MyFilesDetailController({
  jobId,
  documentId,
  documentName,
  clientId,
  downloadFormat,
}: MyFilesDetailControllerProps) {
  const [exportStatus, setExportStatus] = useState<string>(undefined);
  const [error, setError] = useState<boolean>(undefined);

  const dispatch = useAppDispatch();

  const EXPORT_STATUS_RUNNING = 'running';
  const EXPORT_STATUS_SUCCESS = 'success';

  // check if job is open on o2e server
  const { data: jobIsOpen, isError: jobIsOpenError } = useQuery({
    ...queries.web2print.jobIsOpen(documentId, jobId, clientId, downloadFormat.id),
    enabled: !Boolean(error) && exportStatus === EXPORT_STATUS_RUNNING,
    initialData: true,
    refetchOnWindowFocus: false,
    refetchInterval: query => (!Boolean(query.state.data) ? false : 5 * 1000),
    gcTime: 1,
  });

  // start export job if no download formart id is avilable. (e.g. for running jobs)
  const { isSuccess: exportJobSuccess, isError: exportJobError } = useQuery({
    ...queries.web2print.exportJobMyFiles(jobId, clientId),
    enabled:
      !Boolean(error) && !Boolean(downloadFormat.id) && jobIsOpen === false && exportStatus === EXPORT_STATUS_RUNNING,
    refetchOnWindowFocus: false,
    retry: false,
  });
  useEffect(() => {
    if (exportJobSuccess !== true) return;
    setExportStatus(EXPORT_STATUS_SUCCESS);
    dispatch(StatusActions.setSuccessNotification('Document has been added to MyFiles'));
  }, [dispatch, exportJobSuccess]);

  // start export document if download formart id is avilable
  const { isSuccess: exportDocumentSuccess, isError: exportDocumentError } = useQuery({
    ...queries.web2print.exportDocumentMyFiles(documentId, clientId, downloadFormat.id),
    enabled:
      !Boolean(error) && Boolean(downloadFormat.id) && jobIsOpen === false && exportStatus === EXPORT_STATUS_RUNNING,
    refetchOnWindowFocus: false,
    retry: false,
  });
  useEffect(() => {
    if (exportDocumentSuccess !== true) return;
    setExportStatus(EXPORT_STATUS_SUCCESS);
    dispatch(StatusActions.setSuccessNotification('Document has been added to MyFiles'));
  }, [dispatch, exportDocumentSuccess]);

  useEffect(() => {
    if (jobIsOpenError || exportJobError || exportDocumentError) {
      setError(true);
    }
  }, [jobIsOpenError, exportJobError, exportDocumentError]);

  // Handler: onDownload fetches download url and gives it back
  const onExport = useCallback(() => {
    setExportStatus(EXPORT_STATUS_RUNNING);
  }, []);

  return (
    <MyFilesDetail
      isLoading={exportStatus === EXPORT_STATUS_RUNNING}
      onExport={onExport}
      error={error}
      documentName={documentName}
    />
  );
}
